/* eslint-disable max-len */
import type { NavigationFailure, RouteLocationRaw, RouteQueryAndHash, RouteRecordRaw } from 'vue-router'
import { createMemoryHistory, createRouter, createWebHashHistory, createWebHistory } from 'vue-router'

import { ROUTE_HOME } from '~/contexts/home/router/routes'

import { getSwymUrl } from '~/features-shared/config-manager/stores/swym-config'
import { logger } from '~/features-shared/libs/utils/logger'

import type { RouteQuerySwym, RouterExtended } from '~/typings/vue-router'

// import type { RouteLocationCurrentResolved, RouteMap, RouterExtended } from '~/typings/vue-router'

const routes: RouteRecordRaw[] = []

// Redirect all routes not listed to /home
routes.push({ path: '/:pathMatch(.*)*', redirect: '/home' })

// Handle support for old /v3/ routes -> remove /v3 from URL
routes.push({
  path: '/v3/:pathMatch(.*)*',
  redirect(to) {
    return to.fullPath.replace('/v3', '')
  },
})

const _router = createRouter({
  history: window.widget?.id
    ? (import.meta.env.DEV ? createMemoryHistory('/') : createWebHashHistory('/'))
    : createWebHistory(window.SWYM_URL ? new URL(window.SWYM_URL!).pathname : import.meta.env.BASE_URL),
  routes,
  // TODO: Use scrollBehavior to navigate to wiki TOC headings
  /*
  scrollBehavior(to) {
    if (to.hash) {
      return {
        el: to.hash,
        behavior: 'smooth',
      }
    }
  },
  */
})

/**
 * @warning
 * Overload the resolve function so the href always start with SwymURL
 * - This allows to fill all anchor with a full href so link can be opened in new tab from 3DDashboard/MobileApp
 * @example
 * '/home' -> 'https://swym.com/home'
 */
const resolve = _router.resolve
_router.resolve = (to: Parameters<typeof _router.resolve>[0]) => {
  let resolved: ReturnType<typeof resolve>
  logger.info('route', to, 'is resolved')
  try {
    resolved = resolve.apply(_router, [to])
  }
  catch {
    // this catch is needed to prevent not matching route to block the UI rendering

    logger.info('route', to, ' does not exist. Fallback on "/"')
    // fallback on "/". It will have no impact since RouterLink component will ignore this
    return _router.resolve<ROUTE_HOME.LANDING>({ name: ROUTE_HOME.LANDING })
  }
  // If router is in hash mode, replace the hash to generate accurate href
  if (_router.options.history.base.includes('#'))
    resolved.href = resolved.href.replace('#', '')

  // href is now the BASE_URL + "fullPath": /home -> https://swym.com/home
  // OnCloud   => "3dswym.com"         + "/comunity/[id]"
  // OnPremise => "renault.com/3dswym" + "/comunity/[id]"
  resolved.href = `${getSwymUrl()}${resolved.fullPath}`

  return resolved
}

export const router: RouterExtended = Object.assign(_router, {
  goBackOrPush(to: RouteLocationRaw): Promise<NavigationFailure | void | undefined> {
    if (window.history.state?.back)
      return Promise.resolve(_router.back())
    return _router.push(to)
  },
  getQuery <T extends keyof RouteQuerySwym = keyof RouteQuerySwym>(route: RouteQueryAndHash, query: T): RouteQuerySwym[T] | undefined {
    return route.query?.[query] as RouteQuerySwym[T] | undefined
  },
})

// @ts-expect-error only for dev
window.router = router
